import React, { useState } from 'react';
import { calendarSyncApi } from '../services/api';

interface AddToCalendarButtonProps {
  eventType: 'install' | 'follow_up' | 'custom';
  eventId: string;
  className?: string;
}

export function AddToCalendarButton({ eventType, eventId, className = '' }: AddToCalendarButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleGoogleCalendar = async () => {
    try {
      setLoading(true);
      const { url } = await calendarSyncApi.getGoogleCalendarUrl(eventType, eventId);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Failed to get Google Calendar URL:', error);
      alert('Failed to open Google Calendar');
    } finally {
      setLoading(false);
      setIsOpen(false);
    }
  };

  const handleDownloadICS = async () => {
    try {
      setLoading(true);
      await calendarSyncApi.downloadEventICS(eventType, eventId);
    } catch (error) {
      console.error('Failed to download ICS:', error);
      alert('Failed to download calendar file');
    } finally {
      setLoading(false);
      setIsOpen(false);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`px-3 py-1.5 bg-keder-gray hover:bg-gray-600 text-white text-sm rounded transition-colors flex items-center gap-2 ${className}`}
        disabled={loading}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        Add to Calendar
        <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 mt-2 w-56 bg-keder-dark border border-gray-700 rounded-lg shadow-xl z-20">
            <div className="py-1">
              <button
                onClick={handleGoogleCalendar}
                disabled={loading}
                className="w-full text-left px-4 py-2 text-sm text-white hover:bg-keder-gray transition-colors flex items-center gap-3"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                </svg>
                Google Calendar
              </button>
              <button
                onClick={handleDownloadICS}
                disabled={loading}
                className="w-full text-left px-4 py-2 text-sm text-white hover:bg-keder-gray transition-colors flex items-center gap-3"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download .ics File
              </button>
            </div>
            <div className="border-t border-gray-700 px-4 py-2">
              <p className="text-xs text-gray-400">
                Import into Apple Calendar, Outlook, or any calendar app
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
